import type {AttachmentFile, Iso8601, Uuid} from '@hconnect/common/types'
import {Moment} from 'moment-timezone'

import {Approver} from '../../approvers/types/approver.types'
import {User} from '../../types/base.types'
import {Attachment} from '../../types/equipment.types'
import {
  EventStatus,
  StatusListWithApproverStates,
  StatusWithCancellation,
  StatusWithNone,
  StatusWithNoneAndCancellation
} from '../../types/status.types'

import {Stoppage, StoppageDto} from './stoppage.types'
import {
  SharedEventStructure,
  SharedEventStructureDto,
  ShiftEventTaskDto,
  Task,
  TaskChecklistsOption
} from './task.types'

// SAP types
export enum SapStatusCode {
  ON_GOING = 'OSNO',
  PENDING = 'NOPR',
  DONE = 'NOCO',
  SUBMITTED = 'Submitted',
  CREATED = 'ORAS'
}

type SapDataBase<TDate> = {
  sapStatusCode?: SapStatusCode
  sapMaintenanceNotificationId?: string
  lastMaintenanceNotificationCreationStart?: TDate
  sapMaintenanceNotificationCreator?: User
}

export type EventsSummary = {
  generalCount: number
  maintenanceCount: number
  productionCount: number
}

export type SapDataDto = SapDataBase<Iso8601>

export type SapData = SapDataBase<Moment>

export type ParameterChangeData = {
  currentValue?: string
  newValue?: string
}

export type SystemChangeRequestData = {
  currentValue?: string
  newValue?: string
  functionId?: string
}

type ShiftEventCommentBase<TDate> = {
  id: string
  description: string
  createDate: TDate
  isEditable?: boolean
  creator: string
  creatorId: Uuid
}

export type ShiftEventCommentDto = ShiftEventCommentBase<Iso8601>

export type ShiftEventComment = ShiftEventCommentBase<Moment>

export type Comment = ShiftEventComment
export type CommentDto = ShiftEventCommentDto

export interface ShiftEventCommentCreate {
  description: string
}

export type ShiftCommentCreate = ShiftEventCommentCreate & {submittedFor: Iso8601}

// Conflict types
export type ConflictingProperty<T> = {
  name: string
  serverValue: T
  yourValue: T
  delta: number
}

export type ExecutionResultConflict = {
  lastModifiedAt: string
  lastModifiedBy: string
  conflictingProperties: ConflictingProperty<Record<string, unknown>>[]
}

export enum InspectionType {
  Unspecified = 'unspecified',
  Visual = 'visual',
  Thermal = 'thermal',
  Acoustic = 'acoustic'
}
export type SpotInspectionMeasurement = {
  id: string
  name: string
  region: string
  unit: string
  value?: number
  threshold?: number
}
export type SpotInspection = {
  id: string
  inspectionType: InspectionType
  spotInspectionMeasurements: SpotInspectionMeasurement[]
}

export type EventLink = {
  id: string
  eventLinkType: 'orbitPortal'
  linkTargetUrl: string
}

// Event entity types
export type Malfunction = SharedEventStructure & {
  eventType: 'malfunction'
  status: EventStatus<StatusWithCancellation>
}

export type Information = SharedEventStructure & {
  eventType: 'information'
  status: EventStatus<StatusWithNone>
}

export type Idea = SharedEventStructure & {
  eventType: 'idea'
  status: EventStatus<StatusWithNone>
}

export type HealthAndSafety = SharedEventStructure & {
  eventType: 'healthAndSafety'
  status: EventStatus<StatusWithCancellation>
}

export type Environment = SharedEventStructure & {
  eventType: 'environment'
  status: EventStatus<StatusWithCancellation>
}

export type ParameterChange = SharedEventStructure & {
  eventType: 'parameterChange'
  status: EventStatus<StatusListWithApproverStates>
  parameterChange?: ParameterChangeData
  approvers: Approver[]
  needsApproval: boolean
}

export type SystemChangeRequest = SharedEventStructure & {
  eventType: 'systemChangeRequest'
  status: EventStatus<StatusListWithApproverStates>
  parameterChange?: SystemChangeRequestData
  approvers: Approver[]
  needsApproval: boolean
}

export type Quality = SharedEventStructure & {
  eventType: 'quality'
  status: EventStatus<StatusWithCancellation>
}

export type Spot = SharedEventStructure & {
  eventType: 'spot'
  status: EventStatus<StatusWithNone>
  spotInspections: SpotInspection[]
  eventLinks?: EventLink[]
}

type MalfunctionDto = SharedEventStructureDto & {
  eventType: 'malfunction'
  status: EventStatus<StatusWithCancellation>
}

type InformationDto = SharedEventStructureDto & {
  eventType: 'information'
  status: EventStatus<StatusWithNone>
}

type IdeaDto = SharedEventStructureDto & {
  eventType: 'idea'
  status: EventStatus<StatusWithNone>
}

type HealthAndSafetyDto = SharedEventStructureDto & {
  eventType: 'healthAndSafety'
  status: EventStatus<StatusWithNoneAndCancellation>
}

type EnvironmentDto = SharedEventStructureDto & {
  eventType: 'environment'
  status: EventStatus<StatusWithNoneAndCancellation>
}

export type ParameterChangeDto = SharedEventStructureDto & {
  eventType: 'parameterChange'
  status: EventStatus<StatusListWithApproverStates>
  approvers: Approver[]
  parameterChange?: ParameterChangeData
}

export type SystemChangeRequestDto = SharedEventStructureDto & {
  eventType: 'systemChangeRequest'
  status: EventStatus<StatusListWithApproverStates>
  parameterChange?: SystemChangeRequestData
  approvers: Approver[]
}

type QualityDto = SharedEventStructureDto & {
  eventType: 'quality'
  status: EventStatus<StatusWithNoneAndCancellation>
}

type SpotDto = SharedEventStructureDto & {
  eventType: 'spot'
  status: EventStatus<StatusWithNone>
  spotInspections: SpotInspection[]
  eventLinks?: EventLink[]
}

export type BaseEvent =
  | Malfunction
  | Information
  | Idea
  | HealthAndSafety
  | Environment
  | ParameterChange
  | SystemChangeRequest
  | Task
  | Stoppage
  | Quality
  | Spot

export type BaseEventDto =
  | MalfunctionDto
  | InformationDto
  | IdeaDto
  | HealthAndSafetyDto
  | EnvironmentDto
  | ParameterChangeDto
  | SystemChangeRequestDto
  | ShiftEventTaskDto
  | StoppageDto
  | QualityDto
  | SpotDto

type InfoBase<TDate> = {
  createdBy: User
  modifiedBy: string
  created: TDate
  lastModified: TDate
  lastModifiedStatus?: TDate
}

export type InfoDto = InfoBase<Iso8601>

type Info = InfoBase<Moment>

interface Id {
  id: string
}

export type ShiftEventDto = BaseEventDto &
  Id &
  InfoDto &
  SapDataDto & {
    attachments?: Attachment[]
  }

export type ShiftEvent = BaseEvent &
  Id &
  Info &
  SapData & {
    attachments?: Attachment[]
  }

type EventSearchBase<TEvent> = {
  currentPage: number
  events: TEvent[]
  totalItemCount: number
  totalPages: number
}

export type EventSearchDto = EventSearchBase<ShiftEventDto>

export type EventSearchResult = EventSearchBase<ShiftEvent>

export type EventCreate = {
  attachments?: AttachmentFile[]
  splitOptions?: {
    sourceStoppageId: string
    splitPoint: Iso8601
  }
  checklists?: TaskChecklistsOption[]
} & BaseEvent

export type EventCreateDto = {
  attachments?: AttachmentFile[]
} & BaseEventDto

type EventUpdateBase<TDate, TBaseEvent> = {
  id: string
  lastModified: TDate
  attachmentsToRemove: string[]
  newAttachments?: AttachmentFile[]
} & TBaseEvent

export type EventUpdate = EventUpdateBase<Moment, BaseEvent>

export type EventUpdateDto = EventUpdateBase<Iso8601, BaseEventDto>

export type ShiftEventAndUpdateEvent = ShiftEvent & EventUpdate
